import { unwrapResult } from '@reduxjs/toolkit';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import { taptapCreateTicketLogs } from 'src/redux/slices/taptap-customer';
import {
  GetTapTapCustomersPayload,
  TapTapColumn,
  TapTapCustomerData,
  TapTapCustomerPayment,
  TapTapFiltersV2,
  TapTapSendSMSRequest
} from 'src/redux/slices/taptap-customer/types';

const {
  selectors: taptapCustomerSelectors,
  actions: taptapCustomerActions
} = slices.taptapCustomer;

export const useTaptapCustomer = () => {
  const dispatch = useAppDispatch();

  const taptapVersion = useAppSelector(
    taptapCustomerSelectors.selectTapTapVersion
  );

  const isCustomersLoading = useAppSelector(
    taptapCustomerSelectors.selectTaptapCustomerLoading
  );

  const isNextPageLoading = useAppSelector(
    taptapCustomerSelectors.selectTaptapCustomerNextPageLoading
  );

  const selectedSearchValue = useAppSelector(
    taptapCustomerSelectors.selectTaptapCustomerSearchValue
  );

  const selectedGetTaptapPayload = useAppSelector(
    taptapCustomerSelectors.selectTaptapCustomerPayload
  );

  const customers = useAppSelector(taptapCustomerSelectors.selectCustomers);

  const customerProfiles = useAppSelector(
    taptapCustomerSelectors.selectProfiles
  );

  const selectedCustomerFilter = useAppSelector(
    taptapCustomerSelectors.selectTaptapCustomerFilter
  );

  const selectedColumns = useAppSelector(
    taptapCustomerSelectors.selectTaptapCustomerColumns
  );

  //   =========PAYMENTS==========
  const onUpdateLocalCustomerPaymentList = async (data: any) => {
    const newCustomerPaymentData: TapTapCustomerPayment = data?.new ?? {};

    dispatch(
      taptapCustomerActions.insertNewTaptapCustomerPayment(
        newCustomerPaymentData
      )
    );
  };

  const onUpdateLocalCustomerPaymentDetail = async (
    data: TapTapCustomerPayment
  ) => {
    const updatedCustomerPaymentData: TapTapCustomerPayment = data ?? {};
    dispatch(
      taptapCustomerActions.updateTaptapCustomerPaymentDetails(
        updatedCustomerPaymentData
      )
    );
  };

  const onDeleteLocalCustomerPayment = (data: TapTapCustomerData) => {
    const deletedCustomerData: TapTapCustomerData = data?.old ?? {};
    dispatch(
      taptapCustomerActions.removeTaptapCustomerPayment(deletedCustomerData)
    );
  };

  //   =========CUSTOMER CONTROL==========

  const onUpdateCustomerFilter = (tempPopOverFilter: TapTapFiltersV2) => {
    dispatch(
      taptapCustomerActions.updateTaptapCustomerFilter(tempPopOverFilter)
    );
  };

  const updateCustomerPayload = (payload: GetTapTapCustomersPayload) => {
    dispatch(taptapCustomerActions.updateTaptapCustomerGetPayload(payload));
  };

  const onInsertOnLocalCustomerList = async (data: TapTapCustomerData) => {
    const newCustomerData: TapTapCustomerData = data?.new ?? {};
    dispatch(taptapCustomerActions.insertNewTaptapCustomers(newCustomerData));
  };

  //=======COLUMN CONTROLS ==========

  const updateColumn = (column: TapTapColumn[]) => {
    dispatch(taptapCustomerActions.updateTaptapColumns(column || []));
  };

  const resetTaptapColumns = () => {
    dispatch(taptapCustomerActions.resetTaptapColumns());
  };

  //hard reset state of taptap columns
  const onResetAllTapTapState = () => {
    dispatch(taptapCustomerActions.resetAll());
  };

  const updateSearchValue = (input?: string) => {
    dispatch(taptapCustomerActions.updateSearchValue(input || ''));
  };

  //===== NOTIFICATION =======
  const taptapSendNotification = async (data: TapTapSendSMSRequest) => {
    try {
      const smsRes = unwrapResult(
        await dispatch(
          taptapCustomerActions.taptapSendSmsNotifThunk({
            transaction_no: data?.transaction_no ?? '',
            payment_type: data?.payment_type ?? '',
            amount: data?.amount ?? 0,
            action: 'for_confirmation'
          })
        )
      );
      const { success, errors, error, message } = smsRes.originalData;

      if (!success) {
        console.error(
          errors ??
            error ??
            message ??
            'Failed to to send sms notification for this payment'
        );
      }
    } catch (err) {
      console.error(err);
    }
  };

  const createTicketLog = async ({
    ticket_id,
    status,
    updated_by
  }: {
    ticket_id: number;
    status: string;
    updated_by: string;
  }) => {
    try {
      const { error } = await taptapCreateTicketLogs({
        ticket_id,
        status,
        updated_by
      });

      if (error) {
        console.error('Failed to create ticket log:', error);
      }
    } catch (err) {
      console.error(err);
    }
  };

  return {
    taptapVersion,

    isNextPageLoading,
    isCustomersLoading,
    selectedSearchValue,
    selectedGetTaptapPayload,

    customers,
    customerProfiles,
    selectedCustomerFilter,
    selectedColumns,

    //PAYMENT CUSTOMER
    onUpdateLocalCustomerPaymentList,
    onUpdateLocalCustomerPaymentDetail,
    onDeleteLocalCustomerPayment,

    //CONTROLS
    onUpdateCustomerFilter,
    updateCustomerPayload,
    onInsertOnLocalCustomerList,

    //COLUMN CONTROL
    updateSearchValue,
    updateColumn,
    resetTaptapColumns,
    onResetAllTapTapState,

    //NOTIFICATION
    taptapSendNotification,

    //CREATE TICKET LOG
    createTicketLog
  };
};
